"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { MousePointerClick } from "lucide-react";

import { cn } from "@/lib/utils";

type Props = {
  /**
   * Short label of the viewer this gate protects — used in the
   * accessible button label and the on-hover affordance copy
   * ("Click to explore <label>").
   */
  label?: string;
  /**
   * When true, render the gate. Default true. The renderer can pass
   * `false` for surfaces that don't actually trap wheel scroll
   * (videos, static images, link-only embeds) so we don't add a
   * pointless friction layer on top of them.
   */
  active?: boolean;
  /**
   * Extra classes for the outer wrapper. The wrapper is `relative`
   * (so the absolute overlay anchors against it) and `w-full h-full`
   * so it fills the viewer block's height container.
   */
  className?: string;
  /**
   * Canvas-viewer mode. Default `false` (iframe-style behaviour).
   *
   * When `true`, the gate engages on the FIRST wheel event over the
   * gate button — not just on click — and auto-disengages whenever
   * the cursor leaves the wrapper. This is the right shape for
   * canvas-based 3D / 2D viewers (BIM/CAD, point cloud) where the
   * customer expects scroll-to-zoom to "just work" the moment their
   * cursor enters the viewer, AND expects scrolling-past-the-viewer
   * to "just work" the moment their cursor leaves.
   *
   * Why we need a different mode for canvas vs. iframe viewers:
   *
   *   • **Iframe viewers** (Matterport, NavVis IVION, Cesium,
   *     Street View): the iframe owns its document tree, so we can
   *     NOT preventDefault wheel events that already crossed into
   *     the iframe. The page-scroll-trap is unavoidable once the
   *     wheel is captured. The click-to-engage gate is the only
   *     way to give the user agency: "you're in scroll-the-page
   *     mode until you explicitly click the embed."
   *
   *   • **Canvas viewers** (Autodesk Forge V7 + native preventDefault
   *     listener; Potree + OrbitControls preventDefault): wheel
   *     events that reach the canvas are intercepted by our own
   *     code, which preventDefaults the page-scroll. The natural
   *     interaction is identical to Google Earth / Sketchfab /
   *     three.js demos: cursor on the viewer = zooms; cursor off
   *     the viewer = page scrolls. The click-to-engage gate is
   *     UNNECESSARY friction here — and worse, the auto-disengage
   *     on scroll-out makes wheel-zoom feel "unreliable" because
   *     the same gesture (cursor over viewer + wheel) sometimes
   *     zooms (engaged) and sometimes scrolls page (disengaged).
   *
   * Mechanics in this mode:
   *
   *   1. While `engaged === false`, a NATIVE non-passive wheel
   *      listener on the gate button calls `preventDefault()` to
   *      stop the page from scrolling, then sets engaged=true.
   *      The first wheel doesn't reach the canvas (it landed on
   *      the gate button which is at z-20 above), so it produces
   *      no zoom — but every subsequent wheel passes through to
   *      the canvas (`pointer-events-none` once engaged) and the
   *      canvas viewer's own wheel handler runs normally.
   *
   *   2. A `pointerleave` listener on the wrapper resets
   *      engaged=false the instant the cursor leaves the viewer
   *      surface. So when the customer moves their cursor off the
   *      viewer to scroll past it on the page, the gate re-engages
   *      immediately — wheels at the new cursor position fire on
   *      the elements above/below the viewer (cards, text blocks)
   *      where the page's normal scroll behaviour takes over. No
   *      need to wait for the IntersectionObserver auto-disengage
   *      to kick in (which only fires at <20% visibility).
   *
   *   3. The IntersectionObserver auto-disengage stays as a
   *      defensive backstop for keyboard / touch entry points that
   *      don't generate pointerleave events.
   *
   * Click-to-engage still works in this mode (click is a faster
   * path that engages without "losing" the first wheel) — the
   * wheel handler is purely additive.
   */
  engageOnWheel?: boolean;
  children: ReactNode;
};

/**
 * Click-to-explore gate for interactive 3D / map / panorama viewers.
 *
 * Why
 * ---
 * On desktop, scroll-wheel events that land on an interactive viewer
 * (Autodesk BIM, Matterport, NavVis IVION, point cloud, Cesium 3D
 * tiles, Street View) get captured by the viewer for zoom or
 * navigation. The user's page scroll stops mid-block until they move
 * their cursor off — a common "trapped in the embed" feel that the
 * founder flagged as the worst recurring UX issue on /explore.
 *
 * Mechanics
 * ---------
 * The gate puts a transparent button overlay on top of the viewer.
 * The button captures `pointer-events: auto` (which includes wheel
 * scroll) so wheel events that land on the gate bubble up to the
 * page scroll instead of reaching the viewer's wheel handler. A
 * subtle "Click to explore" pill fades in on hover so the affordance
 * is discoverable without dominating the visual.
 *
 * On click, the gate sets `engaged=true`, hides itself, and lets the
 * viewer receive interaction normally. An `IntersectionObserver`
 * watches the wrapper — once it scrolls < 20 % visible (or out
 * entirely), the gate auto-resets to "engaged=false" so the next
 * time the user scrolls back, the protection layer is back.
 *
 * Mobile
 * ------
 * Hidden on `<sm` viewports (`hidden sm:flex`). On mobile we already
 * apply `touch-action: pan-y` to every viewer wrapper so vertical
 * swipes bubble through to the page scroll; the gate would be pure
 * friction on phones and would block the horizontal pan / pinch
 * gestures customers use to look around a 3D model.
 *
 * Limitations
 * -----------
 * `pointer-events` covers click / hover / wheel / contextmenu, but
 * NOT keyboard focus or touch gestures (handled separately above).
 * Some embeds run their own wheel listeners on `window` (rare) — the
 * gate doesn't intercept those. If a specific viewer turns out to do
 * that, we wrap its iframe with a `pointer-events: none` shim while
 * the gate is up.
 */
export function VirtualSpaceExploreGate({
  label,
  active = true,
  className,
  engageOnWheel = false,
  children,
}: Props) {
  const [engaged, setEngaged] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Auto-disengage when the viewer scrolls out of view. Re-engaging
  // lets the next scroll-pass start with the protection layer back
  // up, even if the user never lifts their cursor off the block.
  useEffect(() => {
    if (!engaged) return;
    if (typeof IntersectionObserver === "undefined") return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio < 0.2) {
            setEngaged(false);
          }
        }
      },
      { threshold: [0, 0.2, 0.5] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [engaged]);

  // Canvas-viewer mode: native non-passive wheel listener on the
  // gate button so `preventDefault()` actually fires (React's
  // synthetic onWheel is force-passive on root listeners since
  // Chrome 56 / FF 49 / Safari 11.1, and a passive listener can't
  // preventDefault). Re-attaches whenever `engaged` flips so the
  // listener tears down once the gate's `pointer-events-none` makes
  // the button non-interactive — the canvas's own wheel listener
  // takes over from there.
  useEffect(() => {
    if (!active || !engageOnWheel || engaged) return;
    const btn = buttonRef.current;
    if (!btn) return;
    const handler = (ev: WheelEvent) => {
      // Belt-and-suspenders: stop the page from scrolling under
      // the viewer. The canvas's own preventDefault listener
      // (e.g. autodesk-viewer.tsx's containerRef wheel handler)
      // can't see this wheel because it landed on the gate button
      // — at z-20 above the canvas — so suppression has to happen
      // here.
      ev.preventDefault();
      setEngaged(true);
    };
    btn.addEventListener("wheel", handler, { passive: false });
    return () => btn.removeEventListener("wheel", handler);
  }, [active, engageOnWheel, engaged]);

  // Canvas-viewer mode: re-engage the gate the moment the cursor
  // leaves the viewer surface. Lets the customer scroll past the
  // viewer naturally — they move their cursor up/down off the
  // viewer, the gate snaps back to "needs interaction", and the
  // next wheel at the new cursor position fires on whatever
  // element is there (cards, text blocks, video) where the page's
  // normal scroll behaviour resumes.
  //
  // pointerleave is preferred over mouseleave because it covers
  // mouse + pen + touch from a single listener, fires once per
  // boundary cross (vs. mouseleave which can fire repeatedly when
  // the cursor briefly enters child overlays), and respects
  // pointer capture so a scroll initiated inside the viewer that
  // ends outside it cleanly transitions modes.
  useEffect(() => {
    if (!active || !engageOnWheel || !engaged) return;
    const wrapper = ref.current;
    if (!wrapper) return;
    const onLeave = () => setEngaged(false);
    wrapper.addEventListener("pointerleave", onLeave);
    return () => wrapper.removeEventListener("pointerleave", onLeave);
  }, [active, engageOnWheel, engaged]);

  return (
    <div ref={ref} className={cn("relative h-full w-full", className)}>
      {children}
      {active ? (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setEngaged(true)}
          aria-label={
            label ? `Click to explore ${label}` : "Click to explore this viewer"
          }
          aria-pressed={engaged}
          tabIndex={engaged ? -1 : 0}
          className={cn(
            // Mobile: hidden — touch-action: pan-y handles scroll
            // protection, and forcing a tap-first gesture on a phone
            // is friction with no upside (vertical pan already works
            // through the viewer wrapper).
            "hidden sm:flex",
            "group",
            "absolute inset-0 z-20 items-center justify-center",
            "transition-opacity duration-200 ease-out",
            engaged
              ? "pointer-events-none opacity-0"
              : "pointer-events-auto cursor-pointer bg-background/0 hover:bg-background/10 focus-visible:bg-background/15",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--vs-accent-rgb))]",
          )}
        >
          <span
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium shadow-lg",
              "bg-background/90 text-foreground backdrop-blur-md",
              "border vs-border-soft",
              "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
              "transition-opacity duration-150",
            )}
          >
            <MousePointerClick className="h-4 w-4" aria-hidden />
            Click to explore
          </span>
        </button>
      ) : null}
    </div>
  );
}
